import React, { useState } from 'react';
import { BuildingIcon, SearchIcon, PlusIcon, FilterIcon, MapPinIcon, GlobeIcon, ChevronLeftIcon, ChevronRightIcon, ArrowUpDownIcon, EyeIcon, BarChart3Icon, UsersIcon, DollarSignIcon, PieChartIcon, ClipboardListIcon, BriefcaseIcon, TrendingUpIcon, UserIcon, BellIcon, PhoneIcon, MailIcon, CalendarIcon, ClockIcon, HeartIcon, ThumbsUpIcon, AlertCircleIcon, StarIcon, TagIcon, CheckCircleIcon, MessageSquareIcon, PenIcon, FileTextIcon, HistoryIcon, ArrowRightIcon, ChevronDownIcon, ListIcon, GridIcon, InfoIcon, HelpCircleIcon, RefreshCwIcon, PlusCircleIcon, PercentIcon, LayoutDashboardIcon, ListPlusIcon, ActivityIcon, FolderIcon, SettingsIcon } from 'lucide-react';
import { dealersData } from '../data/dealersData';
import { Link } from 'react-router-dom';
export function DealersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSize, setSelectedSize] = useState('all');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedLifecycleStage, setSelectedLifecycleStage] = useState('all');
  const [selectedEngagementLevel, setSelectedEngagementLevel] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'card', 'pipeline'
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'active', 'at-risk', 'new', 'dormant'
  const [showReminders, setShowReminders] = useState(true);
  const [showOpportunities, setShowOpportunities] = useState(true);
  const [mainTab, setMainTab] = useState('dealers'); // 'overview', 'dealers', 'opportunities', 'reports', 'settings'
  const itemsPerPage = 10;
  // Get unique regions and countries for filters
  const regions = [...new Set(dealersData.map(dealer => dealer.region))];
  const countries = [...new Set(dealersData.map(dealer => dealer.country))];
  // Filter dealers based on search and filters
  const filteredDealers = dealersData.filter(dealer => {
    const matchesSearch = searchQuery === '' || dealer.name.toLowerCase().includes(searchQuery.toLowerCase()) || dealer.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRegion = selectedRegion === 'all' || dealer.region === selectedRegion;
    const matchesCountry = selectedCountry === 'all' || dealer.country === selectedCountry;
    const matchesStatus = selectedStatus === 'all' || true; // Añadir lógica cuando haya datos de estado
    const matchesSize = selectedSize === 'all' || true; // Añadir lógica cuando haya datos de tamaño
    const matchesTab = activeTab === 'all' || activeTab === 'active' && dealer.status === 'active' || activeTab === 'at-risk' && dealer.conversionRate < 50 || activeTab === 'new' && dealer.recentActivity && dealer.recentActivity.some(a => a.type === 'dealer' && a.description.includes('registered')) || activeTab === 'dormant' && dealer.quotesTrend < 0;
    return matchesSearch && matchesRegion && matchesCountry && matchesStatus && matchesSize && matchesTab;
  });
  // Sort dealers
  const sortedDealers = [...filteredDealers].sort((a, b) => {
    if (sortBy === 'name') {
      return sortDirection === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    } else if (sortBy === 'country') {
      return sortDirection === 'asc' ? a.country.localeCompare(b.country) : b.country.localeCompare(a.country);
    } else if (sortBy === 'region') {
      return sortDirection === 'asc' ? a.region.localeCompare(b.region) : b.region.localeCompare(a.region);
    } else if (sortBy === 'quotes') {
      return sortDirection === 'asc' ? a.totalQuotes - b.totalQuotes : b.totalQuotes - a.totalQuotes;
    } else if (sortBy === 'revenue') {
      return sortDirection === 'asc' ? a.totalRevenue - b.totalRevenue : b.totalRevenue - a.totalRevenue;
    } else if (sortBy === 'engagement') {
      const getEngagementScore = dealer => {
        // Calculate engagement score based on quotes, revenue, and trends
        return dealer.totalQuotes * 0.3 + dealer.totalRevenue / 10000 * 0.3 + dealer.quotesTrend * 0.2 + dealer.revenueTrend * 0.2;
      };
      const scoreA = getEngagementScore(a);
      const scoreB = getEngagementScore(b);
      return sortDirection === 'asc' ? scoreA - scoreB : scoreB - scoreA;
    } else if (sortBy === 'lastActivity') {
      // Sort by most recent activity
      if (!a.recentActivity || !b.recentActivity) return 0;
      const dateA = a.recentActivity[0]?.date ? new Date(a.recentActivity[0].date) : new Date(0);
      const dateB = b.recentActivity[0]?.date ? new Date(b.recentActivity[0].date) : new Date(0);
      return sortDirection === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
    }
    return 0;
  });
  // Pagination
  const totalPages = Math.ceil(sortedDealers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDealers = sortedDealers.slice(startIndex, startIndex + itemsPerPage);
  const handleSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  // Calcular métricas para paneles de resumen
  const totalDealers = dealersData.length;
  const totalRevenue = dealersData.reduce((sum, dealer) => sum + dealer.totalRevenue, 0);
  const totalQuotes = dealersData.reduce((sum, dealer) => sum + dealer.totalQuotes, 0);
  const totalActiveQuotes = dealersData.reduce((sum, dealer) => sum + dealer.activeQuotes, 0);
  const totalCustomers = dealersData.reduce((sum, dealer) => sum + dealer.customers, 0);
  // Distribución por región
  const regionDistribution = regions.map(region => {
    const dealersInRegion = dealersData.filter(dealer => dealer.region === region);
    return {
      region,
      count: dealersInRegion.length,
      percentage: dealersInRegion.length / totalDealers * 100
    };
  }).sort((a, b) => b.count - a.count);
  // Top 3 dealers por ingresos
  const topDealers = [...dealersData].sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 3);
  // CRM Metrics
  const activeDealers = dealersData.filter(d => d.status === 'active').length;
  const atRiskDealers = dealersData.filter(d => d.conversionRate < 50).length;
  const newDealers = dealersData.filter(d => d.recentActivity && d.recentActivity.some(a => a.type === 'dealer' && a.description.includes('registered'))).length;
  const dormantDealers = dealersData.filter(d => d.quotesTrend < 0).length;
  // Upcoming interactions & opportunities
  const upcomingInteractions = [{
    id: 1,
    dealerName: "ABE'S IRRIGATION",
    type: 'Call',
    date: 'Tomorrow, 10:00 AM',
    description: 'Quarterly business review',
    priority: 'high'
  }, {
    id: 2,
    dealerName: 'AGROSEEDS LLC',
    type: 'Meeting',
    date: 'Jul 25, 2023',
    description: 'Product training session',
    priority: 'medium'
  }, {
    id: 3,
    dealerName: 'AGRITEC S.A.',
    type: 'Email',
    date: 'Jul 22, 2023',
    description: 'Follow up on quote #AG2023-42',
    priority: 'medium'
  }, {
    id: 4,
    dealerName: 'Adams Mobile Welding LLC',
    type: 'Call',
    date: 'Jul 23, 2023',
    description: 'Discuss new product line',
    priority: 'low'
  }];
  const salesOpportunities = [{
    id: 1,
    dealerName: 'AGROSEEDS LLC',
    value: 120000,
    probability: 75,
    stage: 'Proposal',
    product: 'Center Pivot Package',
    expectedClose: 'Aug 15, 2023'
  }, {
    id: 2,
    dealerName: "ABE'S IRRIGATION",
    value: 85000,
    probability: 60,
    stage: 'Negotiation',
    product: 'Linear Irrigation System',
    expectedClose: 'Aug 30, 2023'
  }, {
    id: 3,
    dealerName: 'AGRO+ SRL',
    value: 165000,
    probability: 40,
    stage: 'Discovery',
    product: 'Complete Farm Solution',
    expectedClose: 'Sep 15, 2023'
  }];
  // Segmentación
  const segments = [{
    name: 'Key Accounts',
    count: Math.round(totalDealers * 0.15),
    color: 'bg-purple-500'
  }, {
    name: 'Growth Potential',
    count: Math.round(totalDealers * 0.25),
    color: 'bg-blue-500'
  }, {
    name: 'Stable',
    count: Math.round(totalDealers * 0.4),
    color: 'bg-green-500'
  }, {
    name: 'At Risk',
    count: Math.round(totalDealers * 0.12),
    color: 'bg-amber-500'
  }, {
    name: 'Declining',
    count: Math.round(totalDealers * 0.08),
    color: 'bg-red-500'
  }];
  // Lifecycle stages
  const lifecycleStages = [{
    name: 'New (0-6 months)',
    count: Math.round(totalDealers * 0.18),
    color: 'bg-emerald-500'
  }, {
    name: 'Growing (6-18 months)',
    count: Math.round(totalDealers * 0.32),
    color: 'bg-blue-500'
  }, {
    name: 'Mature (18+ months)',
    count: Math.round(totalDealers * 0.38),
    color: 'bg-indigo-500'
  }, {
    name: 'Renewal',
    count: Math.round(totalDealers * 0.12),
    color: 'bg-amber-500'
  }];
  // Sample dealer data for table display that matches the image
  const dealerTableData = [{
    id: 1,
    name: 'TEXAS INTERNATIONAL IRRIGATION',
    engagement: 68,
    engagementTrend: '+5%',
    quotes: 32,
    activeQuotes: 18,
    pendingQuotes: 8,
    revenue: 387500,
    revenueTrend: '+22%',
    lastActivity: 'Jul 15, 2023',
    lastActivityDesc: 'New quote #HX6AA6-3 created'
  }, {
    id: 2,
    name: 'UWR',
    engagement: 0,
    engagementStatus: 'Stable',
    quotes: 28,
    activeQuotes: 15,
    pendingQuotes: 7,
    revenue: 325000,
    revenueTrend: '+12%',
    lastActivity: null,
    lastActivityDesc: 'No recent activity'
  }, {
    id: 3,
    name: 'I-BEAM Irrigation',
    engagement: 0,
    engagementStatus: 'Stable',
    quotes: 22,
    activeQuotes: 12,
    pendingQuotes: 5,
    revenue: 267500,
    revenueTrend: '+8%',
    lastActivity: null,
    lastActivityDesc: 'No recent activity'
  }, {
    id: 4,
    name: 'AQUADOC',
    engagement: 0,
    engagementStatus: 'Stable',
    quotes: 18,
    activeQuotes: 10,
    pendingQuotes: 4,
    revenue: 210000,
    revenueTrend: '+15%',
    lastActivity: null,
    lastActivityDesc: 'No recent activity'
  }];
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 to-blue-900" data-id="element-8502" data-mp="5a0sxeznmee">
      <div className="p-6 text-white" data-id="element-8503" data-mp="3rb35lwxc68xt">
        <h1 className="text-2xl font-bold flex items-center" data-id="element-8504" data-mp="3rc4h22i9kxg2">
          <BuildingIcon className="mr-2" size={24} data-id="element-8505" data-mp="3n8yd51imjicr" />
          Dealer Relationship Management
        </h1>
        <p className="text-blue-200 mt-1" data-id="element-8506" data-mp="3i9lgxq5v4yxi">
          Manage your dealer relationships, track engagement, and identify
          growth opportunities
        </p>
      </div>
      {/* Main Navigation Tabs */}
      <div className="px-6" data-id="element-8507" data-mp="26uopk9m7hxmz">
        <div className="flex border-b border-blue-800" data-id="element-8508" data-mp="26xkoa05voqch">
          <MainTab active={mainTab === 'overview'} onClick={() => setMainTab('overview')} icon={<LayoutDashboardIcon size={18} data-id="element-8510" data-mp="rc0lkw03nxwq" />} label="Overview" data-id="element-8509" data-mp="me8jxa9nkxfc" />
          <MainTab active={mainTab === 'dealers'} onClick={() => setMainTab('dealers')} icon={<BuildingIcon size={18} data-id="element-8512" data-mp="vcu9vqhgfeq5" />} label="Dealers" data-id="element-8511" data-mp="ra5xx9xg1roy" />
          <MainTab active={mainTab === 'opportunities'} onClick={() => setMainTab('opportunities')} icon={<TrendingUpIcon size={18} data-id="element-8514" data-mp="154ggtamrern3" />} label="Opportunities" data-id="element-8513" data-mp="1053ucqzsvml1" />
          <MainTab active={mainTab === 'reports'} onClick={() => setMainTab('reports')} icon={<BarChart3Icon size={18} data-id="element-8516" data-mp="1a16wvxykdn8i" />} label="Reports" data-id="element-8515" data-mp="156b1cnu4chqn" />
          <MainTab active={mainTab === 'settings'} onClick={() => setMainTab('settings')} icon={<SettingsIcon size={18} data-id="element-8518" data-mp="1j4811t3vd49j" />} label="Settings" data-id="element-8517" data-mp="1f1uxnbclhifx" />
        </div>
      </div>
      {/* Overview Tab Content */}
      {mainTab === 'overview' && <>
          {/* CRM Navigation Tabs */}
          <div className="px-6 mt-4 mb-4" data-id="element-8519" data-mp="3kut6gw94cua1">
            <div className="flex overflow-x-auto space-x-2 pb-2" data-id="element-8520" data-mp="3kvl4edq6v43h">
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'all' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('all')} data-id="element-8521" data-mp="s02umgqvkscx">
                <BuildingIcon size={16} className="mr-2" data-id="element-8522" data-mp="wyw31urb1t68" />
                All Dealers
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8523" data-mp="1ayf3s2hse28i">
                  {totalDealers}
                </span>
              </button>
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'active' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('active')} data-id="element-8524" data-mp="1kpjhyzzn1j2a">
                <div className="w-2 h-2 rounded-full bg-green-400 mr-2" data-id="element-8525" data-mp="1osohqusc36n8"></div>
                Active
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8526" data-mp="28dgap5hju6vw">
                  {activeDealers}
                </span>
              </button>
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'at-risk' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('at-risk')} data-id="element-8527" data-mp="1zbfg6gdp9yxx">
                <div className="w-2 h-2 rounded-full bg-amber-400 mr-2" data-id="element-8528" data-mp="1fnzmyl4vje60"></div>
                At Risk
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8529" data-mp="1kn2hhbn8a1zq">
                  {atRiskDealers}
                </span>
              </button>
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'new' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('new')} data-id="element-8530" data-mp="16pnmzsi4b40w">
                <div className="w-2 h-2 rounded-full bg-purple-400 mr-2" data-id="element-8531" data-mp="tvdeyjpx6yjj"></div>
                New Dealers
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8532" data-mp="pv3axcloui7b">
                  {newDealers}
                </span>
              </button>
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'dormant' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('dormant')} data-id="element-8533" data-mp="fwdp58qo8dls">
                <div className="w-2 h-2 rounded-full bg-red-400 mr-2" data-id="element-8534" data-mp="259r19k50z5xg"></div>
                Dormant
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8535" data-mp="21arwbzjan960">
                  {dormantDealers}
                </span>
              </button>
            </div>
          </div>
          {/* CRM Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-6 mb-6" data-id="element-8536" data-mp="3swvfewgndeh6">
            {/* Column 1: Performance Metrics */}
            <div className="space-y-4" data-id="element-8537" data-mp="tcbsnpbqqx8">
              {/* Performance Summary */}
              <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8538" data-mp="308r4nwj7yb7m">
                <h3 className="text-blue-300 text-sm font-medium mb-3 flex items-center" data-id="element-8539" data-mp="356iwyrnrzzcc">
                  <BarChart3Icon size={18} className="mr-2 text-blue-300" data-id="element-8540" data-mp="398e6ukjrvl1q" />
                  Performance Overview
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-8541" data-mp="3e9k2or8qbofh">
                  <div className="bg-blue-900/30 rounded-lg p-3" data-id="element-8542" data-mp="oj6n30tzw7fd">
                    <div className="text-blue-300 text-xs mb-1" data-id="element-8543" data-mp="okrl50fiq8hz">
                      Total Dealers
                    </div>
                    <div className="text-white text-xl font-bold" data-id="element-8544" data-mp="2pkmnyq3hmw73">
                      {totalDealers}
                    </div>
                    <div className="text-blue-200 text-xs mt-1" data-id="element-8545" data-mp="2li9ky9g4lce0">
                      <span className="inline-flex items-center" data-id="element-8546" data-mp="2ziu08vmu0q2r">
                        <TrendingUpIcon size={12} className="mr-1 text-green-400" data-id="element-8547" data-mp="2zgzq8cd3midg" />
                        +{newDealers} new
                      </span>
                    </div>
                  </div>
                  <div className="bg-blue-900/30 rounded-lg p-3" data-id="element-8548" data-mp="26sc30b8shix9">
                    <div className="text-blue-300 text-xs mb-1" data-id="element-8549" data-mp="21wmw1mufwzqx">
                      Total Revenue
                    </div>
                    <div className="text-white text-xl font-bold" data-id="element-8550" data-mp="2gmaywsi9s6gr">
                      ${(totalRevenue / 1000).toFixed(0)}k
                    </div>
                    <div className="text-blue-200 text-xs mt-1" data-id="element-8551" data-mp="1o35cw35o1p1f">
                      <span className="inline-flex items-center" data-id="element-8552" data-mp="1j4u4zxyxlolu">
                        <TrendingUpIcon size={12} className="mr-1 text-green-400" data-id="element-8553" data-mp="1j4kco3qez5cz" />
                        +12% YTD
                      </span>
                    </div>
                  </div>
                  <div className="bg-blue-900/30 rounded-lg p-3" data-id="element-8554" data-mp="3f2xgwskcjmb">
                    <div className="text-blue-300 text-xs mb-1" data-id="element-8555" data-mp="3lsakojj0n5ih">
                      Avg. Conversion
                    </div>
                    <div className="text-white text-xl font-bold" data-id="element-8556" data-mp="3ptmc8ulga0tb">62%</div>
                    <div className="text-blue-200 text-xs mt-1" data-id="element-8557" data-mp="hd18ptbl7zxl">
                      <span className="inline-flex items-center" data-id="element-8558" data-mp="mcdvke2gl706">
                        <TrendingUpIcon size={12} className="mr-1 text-green-400" data-id="element-8559" data-mp="mbkyy5x7rxvr" />
                        +5% vs last quarter
                      </span>
                    </div>
                  </div>
                  <div className="bg-blue-900/30 rounded-lg p-3" data-id="element-8560" data-mp="d9uzcct6hsmw">
                    <div className="text-blue-300 text-xs mb-1" data-id="element-8561" data-mp="2t1sgtgagx040">
                      Retention Rate
                    </div>
                    <div className="text-white text-xl font-bold" data-id="element-8562" data-mp="2y386xmri5wt9">85%</div>
                    <div className="text-blue-200 text-xs mt-1" data-id="element-8563" data-mp="3osrwlrx0itdb">
                      <span className="inline-flex items-center" data-id="element-8564" data-mp="3kqn817lf8mxj">
                        <TrendingUpIcon size={12} className="mr-1 text-green-400" data-id="element-8565" data-mp="3kshs6jovcb0k" />
                        +3% vs last year
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Segmentation */}
              <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8566" data-mp="ca24457nck1a">
                <h3 className="text-blue-300 text-sm font-medium mb-3 flex items-center" data-id="element-8567" data-mp="7caa95nfynug">
                  <TagIcon size={18} className="mr-2 text-blue-300" data-id="element-8568" data-mp="2j7d6njggw9i" />
                  Dealer Segmentation
                </h3>
                <div className="space-y-3" data-id="element-8569" data-mp="3tp8sx9ed8e1h">
                  {segments.map(segment => <div key={segment.name} data-id="element-8570" data-mp="v2ny0jyzu0j8">
                      <div className="flex justify-between items-center text-sm mb-1" data-id="element-8571" data-mp="qammj09ngdu9">
                        <span className="text-blue-100" data-id="element-8572" data-mp="xlix0oifed73">{segment.name}</span>
                        <span className="text-blue-200" data-id="element-8573" data-mp="xnlwdz5gxioh">
                          {segment.count} dealers
                        </span>
                      </div>
                      <div className="w-full bg-blue-900/50 rounded-full h-2" data-id="element-8574" data-mp="17innh0jb0utp">
                        <div className={`${segment.color} h-2 rounded-full`} style={{
                    width: `${segment.count / totalDealers * 100}%`
                  }} data-id="element-8575" data-mp="1cj1tye59bwpm"></div>
                      </div>
                    </div>)}
                </div>
                <button className="mt-4 text-xs text-blue-300 hover:text-blue-200 flex items-center" data-id="element-8576" data-mp="1lhdf40ncu2ao">
                  View segmentation details
                  <ChevronRightIcon size={14} className="ml-1" data-id="element-8577" data-mp="1v9j73adlwjpv" />
                </button>
              </div>
            </div>
            {/* Column 2: Relationship Management */}
            <div className="space-y-4" data-id="element-8578" data-mp="249of9mqmjm53">
              {/* Lifecycle Stages */}
              <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8579" data-mp="247ldy6k0u6l2">
                <h3 className="text-blue-300 text-sm font-medium mb-3 flex items-center" data-id="element-8580" data-mp="38p4qc6b8bcz7">
                  <RefreshCwIcon size={18} className="mr-2 text-blue-300" data-id="element-8581" data-mp="3do91h2hprks6" />
                  Lifecycle Stages
                </h3>
                <div className="space-y-3" data-id="element-8582" data-mp="3hq2z11wm48o4">
                  {lifecycleStages.map(stage => <div key={stage.name} data-id="element-8583" data-mp="3moxje8a8a5ac">
                      <div className="flex justify-between items-center text-sm mb-1" data-id="element-8584" data-mp="3mo4gxjiay1y0">
                        <span className="text-blue-100" data-id="element-8585" data-mp="3rmyzckccxohl">{stage.name}</span>
                        <span className="text-blue-200" data-id="element-8586" data-mp="9l0zidoiepf">
                          {stage.count} dealers
                        </span>
                      </div>
                      <div className="w-full bg-blue-900/50 rounded-full h-2" data-id="element-8587" data-mp="5aijxx6ijcrz">
                        <div className={`${stage.color} h-2 rounded-full`} style={{
                    width: `${stage.count / totalDealers * 100}%`
                  }} data-id="element-8588" data-mp="9blzsjok98t7"></div>
                      </div>
                    </div>)}
                </div>
                <div className="mt-4 text-xs text-blue-200 flex items-center" data-id="element-8589" data-mp="1tsbm2g09r3tv">
                  <InfoIcon size={14} className="mr-1" data-id="element-8590" data-mp="1tueltrr84bbs" />
                  Most dealers are in the growth or mature stages
                </div>
              </div>
              {/* Regional Distribution */}
              <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8591" data-mp="2hhmriaskdmec">
                <h3 className="text-blue-300 text-sm font-medium mb-3 flex items-center" data-id="element-8592" data-mp="2dgb1i45s2z5m">
                  <GlobeIcon size={18} className="mr-2 text-blue-300" data-id="element-8593" data-mp="28pcdsjigmysc" />
                  Regional Distribution
                </h3>
                <div className="space-y-3" data-id="element-8594" data-mp="23p6lr28a3a9q">
                  {regionDistribution.slice(0, 4).map(region => <div key={region.region} data-id="element-8595" data-mp="315vi4geglkbq">
                      <div className="flex justify-between items-center text-sm mb-1" data-id="element-8596" data-mp="317q5e1d7doiz">
                        <span className="text-blue-100" data-id="element-8597" data-mp="2wdlxnfw45cgu">{region.region}</span>
                        <span className="text-blue-200" data-id="element-8598" data-mp="3fcgpo2yttnzj">
                          {region.count} dealers
                        </span>
                      </div>
                      <div className="w-full bg-blue-900/50 rounded-full h-2" data-id="element-8599" data-mp="36j3mm871t5zq">
                        <div className="bg-blue-500 h-2 rounded-full" style={{
                    width: `${region.percentage}%`
                  }} data-id="element-8600" data-mp="36h0yjtpghmxn"></div>
                      </div>
                    </div>)}
                </div>
                <button className="mt-4 text-xs text-blue-300 hover:text-blue-200 flex items-center" data-id="element-8601" data-mp="31kabgmf8hr2q">
                  View full regional analysis
                  <ChevronRightIcon size={14} className="ml-1" data-id="element-8602" data-mp="2nlsows12ekme" />
                </button>
              </div>
            </div>
            {/* Column 3: Engagement & Opportunities */}
            <div className="space-y-4" data-id="element-8603" data-mp="lt4jcc0y7l3g">
              {/* Upcoming Interactions */}
              {showReminders && <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8604" data-mp="ptoh9htcisqn">
                  <div className="flex justify-between items-center mb-3" data-id="element-8605" data-mp="2aa61yiedt4vf">
                    <h3 className="text-blue-300 text-sm font-medium flex items-center" data-id="element-8606" data-mp="26byi3eomfng0">
                      <CalendarIcon size={18} className="mr-2 text-blue-300" data-id="element-8607" data-mp="2k33m0sz0v1w5" />
                      Upcoming Interactions
                    </h3>
                    <button onClick={() => setShowReminders(false)} className="text-blue-400 hover:text-blue-300" title="Hide panel" data-id="element-8608" data-mp="2f9imuxlp01vd">
                      <ChevronDownIcon size={18} data-id="element-8609" data-mp="2p1y6dzcn0yid" />
                    </button>
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1" data-id="element-8610" data-mp="33169v4fu2ssd">
                    {upcomingInteractions.map(interaction => <div key={interaction.id} className="bg-blue-900/30 rounded-lg p-3" data-id="element-8611" data-mp="2y34o2br0wfd7">
                        <div className="flex justify-between items-start" data-id="element-8612" data-mp="18ny78qlm9cw6">
                          <div className="flex items-center" data-id="element-8613" data-mp="13obtmg1vlumu">
                            {interaction.type === 'Call' && <PhoneIcon size={14} className="text-blue-400 mr-2" data-id="element-8614" data-mp="16308hab8bsqe" />}
                            {interaction.type === 'Meeting' && <UsersIcon size={14} className="text-green-400 mr-2" data-id="element-8615" data-mp="1a3an1bd37rgv" />}
                            {interaction.type === 'Email' && <MailIcon size={14} className="text-amber-400 mr-2" data-id="element-8616" data-mp="1f4givi21nuum" />}
                            <span className="text-blue-100 text-sm font-medium" data-id="element-8617" data-mp="1k16yk49xsofi">
                              {interaction.type}
                            </span>
                          </div>
                          <div className={`text-xs px-2 py-0.5 rounded-full ${interaction.priority === 'high' ? 'bg-red-900/50 text-red-300' : interaction.priority === 'medium' ? 'bg-amber-900/50 text-amber-300' : 'bg-blue-900/50 text-blue-300'}`} data-id="element-8618" data-mp="rcswndfq97yo">
                            {interaction.priority}
                          </div>
                        </div>
                        <div className="text-white text-sm mt-1" data-id="element-8619" data-mp="8n36j3mwtad3">
                          {interaction.dealerName}
                        </div>
                        <div className="text-blue-200 text-xs mt-1" data-id="element-8620" data-mp="12iq91pnvnsp">
                          {interaction.description}
                        </div>
                        <div className="flex justify-between items-center mt-2" data-id="element-8621" data-mp="3nas5i55v2b4v">
                          <div className="text-blue-300 text-xs flex items-center" data-id="element-8622" data-mp="3ncv61j4n3mnu">
                            <ClockIcon size={12} className="mr-1" data-id="element-8623" data-mp="3ib5vs5p8rd0t" />
                            {interaction.date}
                          </div>
                          <button className="text-xs text-blue-400 hover:text-blue-300" data-id="element-8624" data-mp="39jncvn0wuj41">
                            View details
                          </button>
                        </div>
                      </div>)}
                  </div>
                  <button className="mt-4 w-full py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/50 hover:bg-blue-800/70 rounded-lg flex items-center justify-center" data-id="element-8625" data-mp="2zmr2tcvd5oxd">
                    <PlusIcon size={14} className="mr-1" data-id="element-8626" data-mp="2qlkistti9vzd" />
                    Schedule New Interaction
                  </button>
                </div>}
              {/* Sales Opportunities */}
              {showOpportunities && <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8627" data-mp="2kza7bz3lrc06">
                  <div className="flex justify-between items-center mb-3" data-id="element-8628" data-mp="270kbsnwflkdx">
                    <h3 className="text-blue-300 text-sm font-medium flex items-center" data-id="element-8629" data-mp="2byu9p69jiz38">
                      <TrendingUpIcon size={18} className="mr-2 text-blue-300" data-id="element-8630" data-mp="2zog4zimsku5y" />
                      Sales Opportunities
                    </h3>
                    <button onClick={() => setShowOpportunities(false)} className="text-blue-400 hover:text-blue-300" title="Hide panel" data-id="element-8631" data-mp="33r34m5ppfjdp">
                      <ChevronDownIcon size={18} data-id="element-8632" data-mp="2uw58kpr9fqi5" />
                    </button>
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1" data-id="element-8633" data-mp="3ilzgqu0mwqw2">
                    {salesOpportunities.map(opportunity => <div key={opportunity.id} className="bg-blue-900/30 rounded-lg p-3" data-id="element-8634" data-mp="3ne0ygvgkn9th">
                        <div className="flex justify-between items-start" data-id="element-8635" data-mp="lbz7r8aw5j4">
                          <div className="text-white text-sm font-medium" data-id="element-8636" data-mp="3sqfyxu1l6k31">
                            {opportunity.dealerName}
                          </div>
                          <div className="text-green-300 text-sm font-medium" data-id="element-8637" data-mp="abf5uu8chhzx">
                            ${opportunity.value.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-blue-200 text-xs mt-1" data-id="element-8638" data-mp="3duzzf9i8lvz9">
                          {opportunity.product}
                        </div>
                        <div className="mt-2 flex items-center" data-id="element-8639" data-mp="3nrd04rc79a3i">
                          <div className="w-full bg-blue-900/50 rounded-full h-1.5 mr-2" data-id="element-8640" data-mp="3is03xfzfuqo9">
                            <div className={`${opportunity.probability >= 70 ? 'bg-green-500' : opportunity.probability >= 40 ? 'bg-amber-500' : 'bg-blue-500'} h-1.5 rounded-full`} style={{
                      width: `${opportunity.probability}%`
                    }} data-id="element-8641" data-mp="3iucvmkv00fei"></div>
                          </div>
                          <div className="text-blue-300 text-xs whitespace-nowrap" data-id="element-8642" data-mp="2wjkxa4bnnbty">
                            {opportunity.probability}%
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-2" data-id="element-8643" data-mp="369e9n7jmfuz9">
                          <div className="text-xs px-2 py-0.5 rounded-full bg-blue-800/70 text-blue-300" data-id="element-8644" data-mp="36bh8mh2r4yg4">
                            {opportunity.stage}
                          </div>
                          <div className="text-blue-300 text-xs flex items-center" data-id="element-8645" data-mp="3g5r1s0ziq0rw">
                            <CalendarIcon size={12} className="mr-1" data-id="element-8646" data-mp="3k9p049h0r7jw" />
                            {opportunity.expectedClose}
                          </div>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-4 flex space-x-2" data-id="element-8647" data-mp="1pxilt1stwand">
                    <button className="flex-1 py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/50 hover:bg-blue-800/70 rounded-lg flex items-center justify-center" data-id="element-8648" data-mp="1uv0owbk5zvng">
                      <PlusIcon size={14} className="mr-1" data-id="element-8649" data-mp="1ijq571y1wgb0" />
                      Add Opportunity
                    </button>
                    <button className="flex-1 py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/50 hover:bg-blue-800/70 rounded-lg flex items-center justify-center" data-id="element-8650" data-mp="1dlga0rcbtfpe">
                      View Pipeline
                    </button>
                  </div>
                </div>}
              {/* Hidden panels control */}
              <div className="flex space-x-2" data-id="element-8651" data-mp="218er9r89srod">
                {!showReminders && <button onClick={() => setShowReminders(true)} className="flex-1 py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/30 hover:bg-blue-800/50 rounded-lg flex items-center justify-center" data-id="element-8652" data-mp="1wa3bz0z7hoyr">
                    <CalendarIcon size={14} className="mr-1" data-id="element-8653" data-mp="kxkgtwlyu5xd" />
                    Show Interactions
                  </button>}
                {!showOpportunities && <button onClick={() => setShowOpportunities(true)} className="flex-1 py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/30 hover:bg-blue-800/50 rounded-lg flex items-center justify-center" data-id="element-8654" data-mp="3bqv6i73agtoe">
                    <TrendingUpIcon size={14} className="mr-1" data-id="element-8655" data-mp="3lhhjrunj983x" />
                    Show Opportunities
                  </button>}
              </div>
            </div>
          </div>
        </>}
      {/* Dealers Tab Content */}
      {mainTab === 'dealers' && <>
          {/* Filters */}
          {showFilters && <div className="mx-6 mt-4 mb-4 p-5 bg-gradient-to-r from-blue-900/70 to-indigo-900/70 border border-blue-700 rounded-xl shadow-lg" data-id="element-8656" data-mp="3qf98kfsvse3w">
              <h3 className="text-white text-lg font-medium mb-4 flex items-center" data-id="element-8657" data-mp="88bnzwaex26s">
                <FilterIcon size={18} className="mr-2 text-blue-300" data-id="element-8658" data-mp="3ce4jh3urfps" />
                Advanced CRM Filters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-8659" data-mp="252y1j1v4ks24">
                <div data-id="element-8660" data-mp="255ara174k6pq">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8661" data-mp="kl71uuexhygk">
                    Region
                  </label>
                  <div className="relative" data-id="element-8662" data-mp="pip0nrz8exau">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedRegion} onChange={e => setSelectedRegion(e.target.value)} data-id="element-8663" data-mp="plut9h72khbb">
                      <option value="all" data-id="element-8664" data-mp="6tim8h5h0ruu">All Regions</option>
                      {regions.map(region => <option key={region} value={region} data-id="element-8665" data-mp="6r5zzrsojvbz">
                          {region}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8666" data-mp="3t1i9xskz8zz7">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8667" data-mp="1mb2a4bwqu28w">
                        <polyline points="6 9 12 15 18 9" data-id="element-8668" data-mp="1pq3v692pen09"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8669" data-mp="1fv25nc4inix5">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8670" data-mp="1fuam4zo71bn8">
                    Country
                  </label>
                  <div className="relative" data-id="element-8671" data-mp="28ioi79vq1xw5">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)} data-id="element-8672" data-mp="28hd7eq3uzpvj">
                      <option value="all" data-id="element-8673" data-mp="1uqrurj500c63">All Countries</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-8674" data-mp="2r9z6dja2bwkc">
                          {country}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8675" data-mp="9033zkw6l332">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8676" data-mp="dzxsjhshhgky">
                        <polyline points="6 9 12 15 18 9" data-id="element-8677" data-mp="n0nii4v2ccqr"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8678" data-mp="wxiheov90f5k">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8679" data-mp="wwyyb4tv3kpy">
                    Dealer Segment
                  </label>
                  <div className="relative" data-id="element-8680" data-mp="11qlev835c8r1">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedSegment} onChange={e => setSelectedSegment(e.target.value)} data-id="element-8681" data-mp="15qluux5w8s07">
                      <option value="all" data-id="element-8682" data-mp="120hnhpbjxjab">All Segments</option>
                      {segments.map(segment => <option key={segment.name} value={segment.name.toLowerCase().replace(' ', '-')} data-id="element-8683" data-mp="x2zlkivyc4bt">
                          {segment.name}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8684" data-mp="16rzp8flefnwc">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8685" data-mp="j37135b0qkca">
                        <polyline points="6 9 12 15 18 9" data-id="element-8686" data-mp="t2fqdkuxvasb"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8687" data-mp="24f8gysbztt6s">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8688" data-mp="24eowb3uykqp2">
                    Lifecycle Stage
                  </label>
                  <div className="relative" data-id="element-8689" data-mp="270fvz4hmi652">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedLifecycleStage} onChange={e => setSelectedLifecycleStage(e.target.value)} data-id="element-8690" data-mp="26zei6s9835jn">
                      <option value="all" data-id="element-8691" data-mp="221mukafm25l9">All Lifecycle Stages</option>
                      {lifecycleStages.map(stage => <option key={stage.name} value={stage.name.toLowerCase().replace(/[()]/g, '').replace(/ /g, '-')} data-id="element-8692" data-mp="2prpgs9wughby">
                          {stage.name}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8693" data-mp="2gwsx2nfhzm9u">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8694" data-mp="38p9xgtpgg9lh">
                        <polyline points="6 9 12 15 18 9" data-id="element-8695" data-mp="3nd0hdqmm4lm9"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8696" data-mp="3j92j1i543eu9">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8697" data-mp="3ebk7djzpmlik">
                    Engagement Level
                  </label>
                  <div className="relative" data-id="element-8698" data-mp="39c7kiz8u9efz">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedEngagementLevel} onChange={e => setSelectedEngagementLevel(e.target.value)} data-id="element-8699" data-mp="34de5v3htfhe8">
                      <option value="all" data-id="element-8700" data-mp="2qn11fb44uktn">All Engagement Levels</option>
                      <option value="high" data-id="element-8701" data-mp="2lmveu9cpzoif">High Engagement</option>
                      <option value="medium" data-id="element-8702" data-mp="2lmdcqw3dmg2l">Medium Engagement</option>
                      <option value="low" data-id="element-8703" data-mp="2gpd2e8zhur5o">Low Engagement</option>
                      <option value="inactive" data-id="element-8704" data-mp="26kdeqrq2w21k">Inactive</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8705" data-mp="2biy6e2r9ldee">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8706" data-mp="2gi1ejw2jjzql">
                        <polyline points="6 9 12 15 18 9" data-id="element-8707" data-mp="1nuf01srrzaoy"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8708" data-mp="1xrte5lq87ne3">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8709" data-mp="1xr9v21ouasyh">
                    Status
                  </label>
                  <div className="relative" data-id="element-8710" data-mp="22k2ymm8knv8z">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-8711" data-mp="153e134qnnf5e">
                      <option value="all" data-id="element-8712" data-mp="2k7lu128n2g85">All Statuses</option>
                      <option value="active" data-id="element-8713" data-mp="2k96qixeik98n">Active</option>
                      <option value="pending" data-id="element-8714" data-mp="2y7wjc0uavmr7">Pending Approval</option>
                      <option value="inactive" data-id="element-8715" data-mp="2t81xwctanpdj">Inactive</option>
                      <option value="suspended" data-id="element-8716" data-mp="2t9v2zno9e1jf">Suspended</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8717" data-mp="375qcx40ok2ao">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8718" data-mp="3gxcz1vek3ls7">
                        <polyline points="6 9 12 15 18 9" data-id="element-8719" data-mp="3qvk35dnqy9cs"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8720" data-mp="3lyag24nty9s4">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8721" data-mp="3lummj5idxnde">
                    Dealer Size
                  </label>
                  <div className="relative" data-id="element-8722" data-mp="deqbw7brxtgn">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedSize} onChange={e => setSelectedSize(e.target.value)} data-id="element-8723" data-mp="dccg1w99sd81">
                      <option value="all" data-id="element-8724" data-mp="3lsfdl96o4lin">All Sizes</option>
                      <option value="small" data-id="element-8725" data-mp="3qls1ti7fqqav">Small (1-50 customers)</option>
                      <option value="medium" data-id="element-8726" data-mp="3cu3ojblg9vsb">Medium (51-200 customers)</option>
                      <option value="large" data-id="element-8727" data-mp="3hsohcpydhd6q">Large (201+ customers)</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8728" data-mp="32vnxt9qc4ojc">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8729" data-mp="37uqua5s91mfp">
                        <polyline points="6 9 12 15 18 9" data-id="element-8730" data-mp="7poem068keu6"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8731" data-mp="3u4zkowlot7ln">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8732" data-mp="3u4hk5nrzs77x">
                    Last Interaction
                  </label>
                  <div className="relative" data-id="element-8733" data-mp="3g3wymjuozxaq">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" data-id="element-8734" data-mp="3l4ueuqw56h9l">
                      <option value="all" data-id="element-8735" data-mp="3b6wuiehel4m9">All Time</option>
                      <option value="7days" data-id="element-8736" data-mp="14fxd5i7flkig">Last 7 days</option>
                      <option value="30days" data-id="element-8737" data-mp="14dktn0m0z23a">Last 30 days</option>
                      <option value="90days" data-id="element-8738" data-mp="19dh0d7m3x7mj">Last 90 days</option>
                      <option value="overdue" data-id="element-8739" data-mp="2twjc025wkfaa">Overdue (90+ days)</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8740" data-mp="2oy85a0awmkwa">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8741" data-mp="33nns557be66p">
                        <polyline points="6 9 12 15 18 9" data-id="element-8742" data-mp="3cm9aaz8vjj6y"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-8743" data-mp="3mlqbp4h3fax4">
                <button className="px-4 py-2.5 bg-blue-800 border border-blue-700 rounded-lg text-blue-200 hover:bg-blue-700 transition-colors" onClick={() => {
            setSelectedRegion('all');
            setSelectedCountry('all');
            setSelectedStatus('all');
            setSelectedSize('all');
            setSelectedSegment('all');
            setSelectedLifecycleStage('all');
            setSelectedEngagementLevel('all');
          }} data-id="element-8744" data-mp="3mj42l0ek0ncs">
                  Reset Filters
                </button>
                <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md" onClick={() => setShowFilters(false)} data-id="element-8745" data-mp="65v6gfo85sz">
                  Apply Filters
                </button>
              </div>
            </div>}
          {/* Action bar */}
          <div className="px-6 py-4 flex flex-wrap justify-between items-center gap-4" data-id="element-8746" data-mp="f36qsv0zgkwq">
            <div className="flex items-center gap-3" data-id="element-8747" data-mp="f0tybnxlms5f">
              <div className="relative" data-id="element-8748" data-mp="juyacllq1qdd">
                <input type="text" placeholder="Search dealers..." className="pl-10 pr-4 py-2.5 rounded-lg border border-blue-700 bg-blue-900/70 text-white w-72 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-md" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-8749" data-mp="jw9b0cjvei09" />
                <SearchIcon className="absolute left-3 top-3 text-blue-400" size={18} data-id="element-8750" data-mp="17xl8vnvkx0w9" />
              </div>
              <div className="flex rounded-lg overflow-hidden border border-blue-700 shadow-md" data-id="element-8751" data-mp="12yrw5xo49dx5">
                <button className={`px-3 py-2.5 flex items-center ${viewMode === 'table' ? 'bg-blue-700 text-white' : 'bg-blue-900/70 text-blue-300 hover:bg-blue-800'}`} onClick={() => setViewMode('table')} data-id="element-8752" data-mp="xz5ijn4dlvnt">
                  <ListIcon size={16} className="mr-1" data-id="element-8753" data-mp="1qom6o8kyqqe8" />
                  Table
                </button>
                <button className={`px-3 py-2.5 flex items-center ${viewMode === 'card' ? 'bg-blue-700 text-white' : 'bg-blue-900/70 text-blue-300 hover:bg-blue-800'}`} onClick={() => setViewMode('card')} data-id="element-8754" data-mp="1loq3gbk3b2zq">
                  <GridIcon size={16} className="mr-1" data-id="element-8755" data-mp="1cxhd9o86uucc" />
                  Cards
                </button>
                <button className={`px-3 py-2.5 flex items-center ${viewMode === 'pipeline' ? 'bg-blue-700 text-white' : 'bg-blue-900/70 text-blue-300 hover:bg-blue-800'}`} onClick={() => setViewMode('pipeline')} data-id="element-8756" data-mp="29fm1vrj1wwfy">
                  <TrendingUpIcon size={16} className="mr-1" data-id="element-8757" data-mp="1p49i3pom67oz" />
                  Pipeline
                </button>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-8758" data-mp="1g5fom86kxxfn">
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center px-4 py-2.5 rounded-lg border shadow-md ${showFilters ? 'bg-blue-600 text-white border-blue-500' : 'bg-blue-900/70 text-blue-300 border-blue-700 hover:bg-blue-800'} transition-colors`} data-id="element-8759" data-mp="1g6hbrb0veadq">
                <FilterIcon size={16} className="mr-2" data-id="element-8760" data-mp="2cudkxutza3ex" />
                Filters
                {(selectedRegion !== 'all' || selectedCountry !== 'all' || selectedStatus !== 'all' || selectedSize !== 'all' || selectedSegment !== 'all' || selectedLifecycleStage !== 'all' || selectedEngagementLevel !== 'all') && <span className="ml-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-8761" data-mp="2quy7n230kjdp">
                    {[selectedRegion, selectedCountry, selectedStatus, selectedSize, selectedSegment, selectedLifecycleStage, selectedEngagementLevel].filter(f => f !== 'all').length}
                  </span>}
              </button>
              <button className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg transition-colors shadow-md" data-id="element-8762" data-mp="f609iri1uop7">
                <PlusIcon size={18} className="mr-2" data-id="element-8763" data-mp="oyp8imweya3n" />
                Add Dealer
              </button>
            </div>
          </div>
          {/* Dealers display based on view mode */}
          <div className="flex-1 px-6 pb-6 overflow-auto" data-id="element-8764" data-mp="3sf64innciqla">
            {viewMode === 'table' && <div className="bg-white/5 backdrop-blur-sm border border-blue-800/40 rounded-xl shadow-xl overflow-hidden" data-id="element-8765" data-mp="b5zje9ll8p2b">
                <div className="overflow-x-auto" data-id="element-8766" data-mp="664to9dju5iu">
                  <table className="w-full" data-id="element-8767" data-mp="69ixzn5rwosx">
                    <thead data-id="element-8768" data-mp="1mkd6hc8ip0ha">
                      <tr className="border-b border-blue-800/40" data-id="element-8769" data-mp="1mjlai0b0d0qh">
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8770" data-mp="1hla09ogswo7q">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('name')} data-id="element-8771" data-mp="1oozztrpsdot2">
                            Dealer Name
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'name' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8772" data-mp="1yn642fz2f713" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8773" data-mp="15zjy512d7qb2">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('engagement')} data-id="element-8774" data-mp="1az6c5cq0pakx">
                            Engagement
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'engagement' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8775" data-mp="1kno91iv6exja" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8776" data-mp="s7dkpmqljxfb">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('quotes')} data-id="element-8777" data-mp="35i6ig1seump5">
                            Quotes
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'quotes' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8778" data-mp="31eh0vwa5l5dm" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8779" data-mp="2rki319h447rh">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('revenue')} data-id="element-8780" data-mp="2hrsqeb9tsnuk">
                            Revenue
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'revenue' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8781" data-mp="2domn9ci0fcsb" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8782" data-mp="23unngk5es53j">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('lastActivity')} data-id="element-8783" data-mp="3nnnr4chjpbbi">
                            Last Activity
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'lastActivity' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8784" data-mp="3rog1j925lm9d" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8785" data-mp="3iqnn0749ain1">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-blue-800/30" data-id="element-8786" data-mp="2uzi75rybfqcq">
                      {dealerTableData.map(dealer => <tr key={dealer.id} className="hover:bg-blue-800/20 transition-colors" data-id="element-8787" data-mp="2zxjp29k09jmm">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8788" data-mp="2l12bdbocfsww">
                            <div className="flex items-center" data-id="element-8789" data-mp="2l353c1923w49">
                              <div className="flex-shrink-0 h-10 w-10 bg-blue-900/30 rounded-full flex items-center justify-center border border-blue-700/30" data-id="element-8790" data-mp="2pykh6t72ds0o">
                                <BuildingIcon className="h-5 w-5 text-blue-500" aria-hidden="true" data-id="element-8791" data-mp="22crzdplgz3gu" />
                              </div>
                              <div className="ml-4" data-id="element-8792" data-mp="2gd2nwlzb45yu">
                                <div className="text-sm font-medium text-white" data-id="element-8793" data-mp="2bdgb2dne4rqk">
                                  {dealer.name}
                                </div>
                                <div className="text-xs text-blue-300" data-id="element-8794" data-mp="2pb4tzfxwdosz">
                                  ID: {dealer.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8795" data-mp="2u3elh0enuwxd">
                            <div className="flex items-center" data-id="element-8796" data-mp="390p3ytpq132l">
                              {dealer.engagement > 0 ? <>
                                  <div className="relative h-2 w-24 bg-blue-900/50 rounded mr-2" data-id="element-8797" data-mp="342nbji6enreg">
                                    <div className="absolute top-0 left-0 h-2 rounded bg-blue-500" style={{
                            width: `${dealer.engagement}%`
                          }} data-id="element-8798" data-mp="9bemsnhnfpnd"></div>
                                  </div>
                                  <span className="text-sm text-white" data-id="element-8799" data-mp="ckwtfytpxis">
                                    {dealer.engagement}%
                                  </span>
                                </> : <span className="text-sm text-blue-300" data-id="element-8800" data-mp="3migfg64p30la">
                                  {dealer.engagementStatus || '0%'}
                                </span>}
                            </div>
                            {dealer.engagementTrend && <div className="text-xs text-green-400 mt-1 flex items-center" data-id="element-8801" data-mp="3cpiqpi940fe8">
                                <TrendingUpIcon size={12} className="mr-1" data-id="element-8802" data-mp="38ogqoz7dbzb7" />
                                {dealer.engagementTrend}
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8803" data-mp="2yuge6c9rbrsg">
                            <div className="flex items-center text-sm text-white" data-id="element-8804" data-mp="1e9jnul5a9o92">
                              <BarChart3Icon size={16} className="mr-1 text-blue-500" data-id="element-8805" data-mp="1j839ekfysy0w" />
                              {dealer.quotes}
                            </div>
                            <div className="text-xs text-blue-300 mt-1" data-id="element-8806" data-mp="1s9kpi2tdgzpl">
                              <span className="inline-flex items-center" data-id="element-8807" data-mp="1s7i2zsrfhopm">
                                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" data-id="element-8808" data-mp="vl4wfwr2087q"></span>
                                Active: {dealer.activeQuotes}
                              </span>
                              {' · '}
                              <span className="inline-flex items-center" data-id="element-8809" data-mp="19aqbwx5na5b0">
                                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" data-id="element-8810" data-mp="19ctai5kv56rc"></span>
                                Pending: {dealer.pendingQuotes}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8811" data-mp="1k40fmy11gle0">
                            <div className="text-sm text-white" data-id="element-8812" data-mp="1f7a14f4vtxup">
                              ${dealer.revenue.toLocaleString()}
                            </div>
                            <div className="text-xs text-green-400 mt-1 flex items-center" data-id="element-8813" data-mp="1owbgq47imd85">
                              <TrendingUpIcon size={12} className="mr-1" data-id="element-8814" data-mp="1oynwqbtpqdik" />
                              {dealer.revenueTrend}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8815" data-mp="w9qs3bifcf8a">
                            {dealer.lastActivity ? <div data-id="element-8816" data-mp="1a5aw70thusvn">
                                <div className="text-sm text-white" data-id="element-8817" data-mp="165ajplpygrr8">
                                  {dealer.lastActivity}
                                </div>
                                <div className="text-xs text-blue-300 mt-1" data-id="element-8818" data-mp="2londuunxikcr">
                                  {dealer.lastActivityDesc}
                                </div>
                              </div> : <div className="text-sm text-blue-300" data-id="element-8819" data-mp="2p2x3pi47f9ed">
                                {dealer.lastActivityDesc}
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8820" data-mp="2f7vcmgqdbx95">
                            <div className="flex justify-end space-x-2" data-id="element-8821" data-mp="2k5x51s9op8xa">
                              <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-1.5 rounded-md transition-colors" data-id="element-8822" data-mp="37w0xr4lc3o9i">
                                <PhoneIcon size={16} data-id="element-8823" data-mp="3cvx5nex5jzuc" />
                              </button>
                              <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-1.5 rounded-md transition-colors" data-id="element-8824" data-mp="2y3bzxcipi8wn">
                                <MailIcon size={16} data-id="element-8825" data-mp="3qt24ouyg7yg3" />
                              </button>
                              <Link to={`/dealers/${dealer.id}`} className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 px-3 py-1.5 rounded-md transition-colors inline-flex items-center" data-id="element-8826" data-mp="3vllt6myocdzo">
                                <EyeIcon size={16} className="mr-1" data-id="element-8827" data-mp="22wcmz739jt1c" />
                                View
                              </Link>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="bg-blue-900/20 px-6 py-3 flex items-center justify-between border-t border-blue-800/30" data-id="element-8828" data-mp="2lnndvo8zo5t8">
                  <div className="flex-1 flex justify-between items-center" data-id="element-8829" data-mp="2gwf2wns0gcdy">
                    <div data-id="element-8830" data-mp="2bxu1wm4xpoog">
                      <p className="text-sm text-blue-300" data-id="element-8831" data-mp="2bym106xqq4jh">
                        Showing{' '}
                        <span className="font-medium text-white" data-id="element-8832" data-mp="39dhqq5tb5udh">1</span> to{' '}
                        <span className="font-medium text-white" data-id="element-8833" data-mp="34mrm4usphvju">
                          {dealerTableData.length}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium text-white" data-id="element-8834" data-mp="3cxi3mmfe3h3a">
                          {totalDealers}
                        </span>{' '}
                        dealers
                      </p>
                    </div>
                    <div data-id="element-8835" data-mp="3qvg8n17tm164">
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-8836" data-mp="3qvq06t8ikgdx">
                        <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-blue-700 bg-blue-900/20 text-sm font-medium text-blue-300 hover:bg-blue-800/30" data-id="element-8837" data-mp="3vtrj9e5xwfpe">
                          <span className="sr-only" data-id="element-8838" data-mp="8hpeest9abap">Previous</span>
                          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-8839" data-mp="dez0pzlcm6ub" />
                        </button>
                        <button className="relative inline-flex items-center px-4 py-2 border border-blue-700 bg-blue-700 text-sm font-medium text-white" data-id="element-8840" data-mp="igeq23uk6zii">
                          1
                        </button>
                        <button className="relative inline-flex items-center px-4 py-2 border border-blue-700 bg-blue-900/20 text-sm font-medium text-blue-300 hover:bg-blue-800/30" data-id="element-8841" data-mp="3tvq48f5p0iye">
                          2
                        </button>
                        <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-blue-700 bg-blue-900/20 text-sm font-medium text-blue-300 hover:bg-blue-800/30" data-id="element-8842" data-mp="2olzxs0xnato">
                          <span className="sr-only" data-id="element-8843" data-mp="bhpj8gy9ehw8">Next</span>
                          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-8844" data-mp="bjjwd93ah5pr" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>}
            {viewMode === 'card' && <div data-id="element-8845" data-mp="2s5bchcikxipx">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8846" data-mp="2s6w875gmr7pd">
                  {paginatedDealers.map(dealer => <div key={dealer.id} className="bg-white/5 backdrop-blur-sm border border-blue-800/40 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow" data-id="element-8847" data-mp="2n93026vro3mg">
                      <div className="p-5 border-b border-blue-800/30" data-id="element-8848" data-mp="17niw6ze95554">
                        <div className="flex justify-between items-start" data-id="element-8849" data-mp="szxsp4dgt3wo">
                          <div className="flex items-center" data-id="element-8850" data-mp="xxzc5qesz58o">
                            <div className="h-12 w-12 bg-blue-900/30 rounded-full flex items-center justify-center border border-blue-700/30" data-id="element-8851" data-mp="xwekc8jvi0ei">
                              <BuildingIcon className="h-6 w-6 text-blue-500" data-id="element-8852" data-mp="o0kw3rwskigw" />
                            </div>
                            <div className="ml-3" data-id="element-8853" data-mp="a13ga21h4fjo">
                              <h3 className="text-lg font-medium text-white" data-id="element-8854" data-mp="a2efdojz4z4g">
                                {dealer.name}
                              </h3>
                              <div className="flex items-center text-sm text-blue-300" data-id="element-8855" data-mp="24fcnc1i7bjom">
                                <MapPinIcon size={14} className="mr-1" data-id="element-8856" data-mp="29dnx6c8hxu6u" />
                                {dealer.country}
                              </div>
                            </div>
                          </div>
                          {dealer.status === 'active' && <span className="bg-green-900/30 text-green-400 border border-green-700/30 text-xs px-2 py-1 rounded-full" data-id="element-8857" data-mp="s7bwuk0uu6ts">
                              Active
                            </span>}
                          {dealer.status === 'pending' && <span className="bg-amber-900/30 text-amber-400 border border-amber-700/30 text-xs px-2 py-1 rounded-full" data-id="element-8858" data-mp="dgbqqlnkyp0t">
                              Pending
                            </span>}
                          {dealer.status === 'inactive' && <span className="bg-blue-900/30 text-blue-400 border border-blue-700/30 text-xs px-2 py-1 rounded-full" data-id="element-8859" data-mp="4fncpqy6sonx">
                              Inactive
                            </span>}
                        </div>
                      </div>
                      <div className="p-5" data-id="element-8860" data-mp="1tu1wrxy9fnd3">
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-8861" data-mp="1ts8mm8oful0c">
                          <div data-id="element-8862" data-mp="21d1fs2ht7cvx">
                            <div className="text-xs text-blue-300 mb-1" data-id="element-8863" data-mp="21bq7pqhbziz0">
                              Total Revenue
                            </div>
                            <div className="text-lg font-medium text-white" data-id="element-8864" data-mp="2b4ozfh84jpmu">
                              ${dealer.totalRevenue.toLocaleString()}
                            </div>
                            <div className="text-xs text-blue-300 mt-1 flex items-center" data-id="element-8865" data-mp="1ifi1ajcl1224">
                              {dealer.revenueTrend > 0 ? <span className="text-green-400 flex items-center" data-id="element-8866" data-mp="1nf4g2x826qd1">
                                  <TrendingUpIcon size={12} className="mr-1" data-id="element-8867" data-mp="1scmnukacazjg" />+
                                  {dealer.revenueTrend}%
                                </span> : dealer.revenueTrend < 0 ? <span className="text-red-400 flex items-center" data-id="element-8868" data-mp="33sbeblmsygp0">
                                  <TrendingUpIcon size={12} className="mr-1 rotate-180" data-id="element-8869" data-mp="33ue0fuku3pog" />
                                  {dealer.revenueTrend}%
                                </span> : <span className="text-blue-300" data-id="element-8870" data-mp="38qkdaafs1u6g">Stable</span>}
                            </div>
                          </div>
                          <div data-id="element-8871" data-mp="3cr499aom6rr0">
                            <div className="text-xs text-blue-300 mb-1" data-id="element-8872" data-mp="3hk773v0fcnd0">
                              Quotes
                            </div>
                            <div className="text-lg font-medium text-white" data-id="element-8873" data-mp="3miifs2ezgrtn">
                              {dealer.totalQuotes}
                            </div>
                            <div className="text-xs text-blue-300 mt-1" data-id="element-8874" data-mp="2ttt9zq2zfybk">
                              Active: {dealer.activeQuotes} / Pending:{' '}
                              {dealer.pendingQuotes || 0}
                            </div>
                          </div>
                        </div>
                        <div className="mb-4" data-id="element-8875" data-mp="e3qcnyp54exh">
                          <div className="flex justify-between items-center mb-1" data-id="element-8876" data-mp="e2yrzix301lz">
                            <div className="text-xs text-blue-300" data-id="element-8877" data-mp="j10c8768u6z1">
                              Engagement
                            </div>
                            <div className="text-xs font-medium text-white" data-id="element-8878" data-mp="1yl98f3r6zdjz">
                              {dealer.conversionRate || 0}%
                            </div>
                          </div>
                          <div className="w-full h-2 bg-blue-900/50 rounded-full overflow-hidden" data-id="element-8879" data-mp="1ovfwu2r1uyfq">
                            <div className={`h-2 ${dealer.conversionRate >= 70 ? 'bg-green-500' : dealer.conversionRate >= 50 ? 'bg-blue-500' : dealer.conversionRate >= 30 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                      width: `${dealer.conversionRate || 0}%`
                    }} data-id="element-8880" data-mp="1ktupsy175edg"></div>
                          </div>
                        </div>
                        {dealer.recentActivity && dealer.recentActivity[0] && <div className="mb-4" data-id="element-8881" data-mp="35a7o2ccglzng">
                            <div className="text-xs text-blue-300 mb-1" data-id="element-8882" data-mp="357lksowlq6b1">
                              Last Activity
                            </div>
                            <div className="text-sm text-white" data-id="element-8883" data-mp="yl5qfr0zhe9r">
                              {dealer.recentActivity[0].description}
                            </div>
                            <div className="text-xs text-blue-300 mt-1" data-id="element-8884" data-mp="on9if83cezfv">
                              {dealer.recentActivity[0].date}
                            </div>
                          </div>}
                        <div className="flex justify-between items-center mt-4" data-id="element-8885" data-mp="1hbx95j6v2h0t">
                          <div className="flex space-x-2" data-id="element-8886" data-mp="1h9u720sfoxfq">
                            <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-2 rounded-md transition-colors" data-id="element-8887" data-mp="1m9ys844aegca">
                              <PhoneIcon size={16} data-id="element-8888" data-mp="18dlbwybhokze" />
                            </button>
                            <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-2 rounded-md transition-colors" data-id="element-8889" data-mp="1cewysw2zas3w">
                              <MailIcon size={16} data-id="element-8890" data-mp="yq8ko4naelo" />
                            </button>
                            <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-2 rounded-md transition-colors" data-id="element-8891" data-mp="3pir07q7huhu2">
                              <CalendarIcon size={16} data-id="element-8892" data-mp="3kmjqdmbz8m6u" />
                            </button>
                          </div>
                          <Link to={`/dealers/${dealer.id}`} className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 px-3 py-2 rounded-md transition-colors inline-flex items-center" data-id="element-8893" data-mp="3uiwjahzp6w0j">
                            <EyeIcon size={16} className="mr-1" data-id="element-8894" data-mp="31uhip0hbcg8q" />
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>)}
                </div>
                {/* Pagination */}
                {totalPages > 1 && <div className="flex justify-between items-center py-4 mt-4 bg-white/5 backdrop-blur-sm rounded-xl px-6 shadow-md" data-id="element-8895" data-mp="2o2b3bglzidac">
                    <button className="flex items-center text-blue-300 hover:text-blue-200 disabled:text-blue-500/50" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} data-id="element-8896" data-mp="2j2ooj2qicozf">
                      <ChevronLeftIcon size={16} className="mr-1" data-id="element-8897" data-mp="1qe7s0zctm48p" />
                      Previous
                    </button>
                    <div className="flex items-center gap-1" data-id="element-8898" data-mp="1vay99q0d35vp">
                      {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} className={`w-8 h-8 rounded-full flex items-center justify-center ${currentPage === page ? 'bg-blue-600 text-white' : 'text-blue-300 hover:bg-blue-800/50'}`} onClick={() => setCurrentPage(page)} data-id="element-8899" data-mp="251sv4t9p43gm">
                          {page}
                        </button>)}
                    </div>
                    <button className="flex items-center text-blue-300 hover:text-blue-200 disabled:text-blue-500/50" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} data-id="element-8900" data-mp="2j09crf93jedj">
                      Next
                      <ChevronRightIcon size={16} className="ml-1" data-id="element-8901" data-mp="h53b5qubq5sd" />
                    </button>
                  </div>}
              </div>}
            {viewMode === 'pipeline' && <div className="bg-white/5 backdrop-blur-sm border border-blue-800/40 rounded-xl shadow-xl overflow-hidden p-6" data-id="element-8902" data-mp="7f062s9xh1do">
                <h3 className="text-lg font-medium text-white mb-4" data-id="element-8903" data-mp="2gowminjosvz">
                  Dealer Pipeline View
                </h3>
                <p className="text-blue-300 mb-6" data-id="element-8904" data-mp="1035rzicpwwf3">
                  Visualize your dealer relationships across different stages of
                  the lifecycle
                </p>
                <div className="flex overflow-x-auto pb-4 space-x-4" data-id="element-8905" data-mp="q4gfk53lc468">
                  {/* New Dealers Column */}
                  <div className="min-w-[300px] bg-purple-900/20 rounded-lg border border-purple-700/30 flex flex-col" data-id="element-8906" data-mp="m3d19n16yg74">
                    <div className="p-4 border-b border-purple-700/30 bg-purple-900/30" data-id="element-8907" data-mp="3atmgrdlofnqp">
                      <h4 className="font-medium text-purple-300 flex items-center" data-id="element-8908" data-mp="36su6ch12jcsu">
                        <PlusCircleIcon size={18} className="mr-2" data-id="element-8909" data-mp="38bu2t0cvyjau" />
                        New Dealers
                        <span className="ml-2 bg-purple-900/50 text-purple-300 border border-purple-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8910" data-mp="3d9m60qizdruc">
                          {newDealers}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8911" data-mp="2pkjd2miwcckj">
                      {dealersData.filter(d => d.recentActivity && d.recentActivity.some(a => a.type === 'dealer' && a.description.includes('registered'))).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-purple-700/30" data-id="element-8912" data-mp="2fogdnoffis1a">
                            <div className="font-medium text-white" data-id="element-8913" data-mp="26lfa9vhy7f1b">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8914" data-mp="2amr124ck66b3">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8915" data-mp="1rzrwqt6938rf" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 text-xs text-purple-300" data-id="element-8916" data-mp="20zoe09qb95cg">
                              New dealer onboarding in progress
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8917" data-mp="1w36gnuxb89a5">
                              <div className="text-xs text-blue-300" data-id="element-8918" data-mp="2aw9k6ye2gmhv">
                                {dealer.recentActivity && dealer.recentActivity[0]?.date}
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-purple-400 hover:text-purple-300 text-xs" data-id="element-8919" data-mp="2jpmlooq74wfk">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* Growing Dealers Column */}
                  <div className="min-w-[300px] bg-blue-900/20 rounded-lg border border-blue-700/30 flex flex-col" data-id="element-8920" data-mp="3w2pz7ip03iur">
                    <div className="p-4 border-b border-blue-700/30 bg-blue-900/30" data-id="element-8921" data-mp="3wujhdelpf5o">
                      <h4 className="font-medium text-blue-300 flex items-center" data-id="element-8922" data-mp="8wyz717oq5uu">
                        <TrendingUpIcon size={18} className="mr-2" data-id="element-8923" data-mp="dwv5x87robe3" />
                        Growing
                        <span className="ml-2 bg-blue-900/50 text-blue-300 border border-blue-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8924" data-mp="3ceh7f9rddevs">
                          {dealersData.filter(d => d.quotesTrend > 10).length}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8925" data-mp="3r3dgna55wxxz">
                      {dealersData.filter(d => d.quotesTrend > 10).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-blue-700/30" data-id="element-8926" data-mp="2tn7pcc3j9hsl">
                            <div className="font-medium text-white" data-id="element-8927" data-mp="2r3l3pm8hehql">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8928" data-mp="2m4q9zp8z78rp">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8929" data-mp="2h3amzrn8ms6o" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 flex items-center text-sm text-blue-300" data-id="element-8930" data-mp="283xep2auo9nj">
                              <TrendingUpIcon size={14} className="mr-1" data-id="element-8931" data-mp="23dgwl4w33zuu" />
                              {dealer.quotesTrend}% growth in quotes
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8932" data-mp="1yc9tvk8stqv1">
                              <div className="text-xs text-blue-300" data-id="element-8933" data-mp="1th47ylfecc6y">
                                ${dealer.totalRevenue.toLocaleString()} revenue
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-blue-400 hover:text-blue-300 text-xs" data-id="element-8934" data-mp="1kf4pdooqh01w">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* Stable Dealers Column */}
                  <div className="min-w-[300px] bg-green-900/20 rounded-lg border border-green-700/30 flex flex-col" data-id="element-8935" data-mp="3ljycbnqbvjli">
                    <div className="p-4 border-b border-green-700/30 bg-green-900/30" data-id="element-8936" data-mp="2xtb38z4oenxd">
                      <h4 className="font-medium text-green-300 flex items-center" data-id="element-8937" data-mp="2svjak2w7ixs4">
                        <CheckCircleIcon size={18} className="mr-2" data-id="element-8938" data-mp="36meer1vrgqpd" />
                        Stable
                        <span className="ml-2 bg-green-900/50 text-green-300 border border-green-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8939" data-mp="31vxyla0k2qzb">
                          {dealersData.filter(d => d.quotesTrend >= 0 && d.quotesTrend <= 10).length}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8940" data-mp="200bdjlsi0lzd">
                      {dealersData.filter(d => d.quotesTrend >= 0 && d.quotesTrend <= 10).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-green-700/30" data-id="element-8941" data-mp="1q2f36w7dxv2b">
                            <div className="font-medium text-white" data-id="element-8942" data-mp="1v29xl9qdd2s4">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8943" data-mp="2nq5p801304i1">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8944" data-mp="2nntb5xyg2ea9" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 text-xs text-green-300" data-id="element-8945" data-mp="2dqyap9im23tc">
                              Consistent performance
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8946" data-mp="12ckumqu3msnv">
                              <div className="text-xs text-blue-300" data-id="element-8947" data-mp="12agjbntverdk">
                                {dealer.totalQuotes} total quotes
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-green-400 hover:text-green-300 text-xs" data-id="element-8948" data-mp="3016t2evv6t2i">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* At Risk Dealers Column */}
                  <div className="min-w-[300px] bg-amber-900/20 rounded-lg border border-amber-700/30 flex flex-col" data-id="element-8949" data-mp="2h9dzojcvv5u9">
                    <div className="p-4 border-b border-amber-700/30 bg-amber-900/30" data-id="element-8950" data-mp="2cbvn8iznq8hi">
                      <h4 className="font-medium text-amber-300 flex items-center" data-id="element-8951" data-mp="27cj52bjqdpl9">
                        <AlertCircleIcon size={18} className="mr-2" data-id="element-8952" data-mp="ajzf7jcp7grh" />
                        At Risk
                        <span className="ml-2 bg-amber-900/50 text-amber-300 border border-amber-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8953" data-mp="5juxxr3yui07">
                          {atRiskDealers}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8954" data-mp="i0vz8ed4a2tg">
                      {dealersData.filter(d => d.conversionRate < 50).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-amber-700/30" data-id="element-8955" data-mp="rrql3hmgb0ed">
                            <div className="font-medium text-white" data-id="element-8956" data-mp="3qcs2upbgriz2">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8957" data-mp="42xsnv51faoi">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8958" data-mp="92k8mcc93510" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 text-xs text-amber-300 flex items-center" data-id="element-8959" data-mp="1fkgrzcfhyths">
                              <AlertCircleIcon size={14} className="mr-1" data-id="element-8960" data-mp="1kfmgmj0aam9k" />
                              Low conversion rate: {dealer.conversionRate || 0}%
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8961" data-mp="1e2s0gw8v30sh">
                              <div className="text-xs text-blue-300" data-id="element-8962" data-mp="1s1rktrpcctiu">
                                Needs attention
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-amber-400 hover:text-amber-300 text-xs" data-id="element-8963" data-mp="211eoyl9ggdfl">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* Dormant Dealers Column */}
                  <div className="min-w-[300px] bg-red-900/20 rounded-lg border border-red-700/30 flex flex-col" data-id="element-8964" data-mp="25swpj87kgc01">
                    <div className="p-4 border-b border-red-700/30 bg-red-900/30" data-id="element-8965" data-mp="2knjeegou5qge">
                      <h4 className="font-medium text-red-300 flex items-center" data-id="element-8966" data-mp="2fs3zdlf3doie">
                        <ClockIcon size={18} className="mr-2" data-id="element-8967" data-mp="2zdntrnkl5gp9" />
                        Dormant
                        <span className="ml-2 bg-red-900/50 text-red-300 border border-red-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8968" data-mp="33cwige0rqody">
                          {dormantDealers}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8969" data-mp="2fodajzlmsdme">
                      {dealersData.filter(d => d.quotesTrend < 0).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-red-700/30" data-id="element-8970" data-mp="2kfvcapvhai8f">
                            <div className="font-medium text-white" data-id="element-8971" data-mp="2bmruk5dsvj7z">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8972" data-mp="1wqsof0a93t5h">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8973" data-mp="21nj3ple8ekpu" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 text-xs text-red-300 flex items-center" data-id="element-8974" data-mp="1ilsfbefbjskt">
                              <TrendingUpIcon size={14} className="mr-1 rotate-180" data-id="element-8975" data-mp="1dr62r7xi3tz5" />
                              {Math.abs(dealer.quotesTrend)}% decline in
                              activity
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8976" data-mp="21jd28fyiwh2s">
                              <div className="text-xs text-blue-300" data-id="element-8977" data-mp="1wjqm9yrb8mqa">
                                Last active:{' '}
                                {dealer.recentActivity && dealer.recentActivity[0]?.date}
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-red-400 hover:text-red-300 text-xs" data-id="element-8978" data-mp="1mkrf6km70z5e">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </>}
      {/* Other tabs content */}
      {(mainTab === 'opportunities' || mainTab === 'reports' || mainTab === 'settings') && <div className="flex items-center justify-center h-64 p-6" data-id="element-8979" data-mp="2wrng5lcqr6ru">
          <div className="text-center text-white" data-id="element-8980" data-mp="31incaoe1dkta">
            <h2 className="text-xl font-medium mb-2" data-id="element-8981" data-mp="31k85a9kpcvp1">
              {mainTab.charAt(0).toUpperCase() + mainTab.slice(1)} Module
            </h2>
            <p className="text-blue-300" data-id="element-8982" data-mp="3ajbrhmf61r7r">This section is under development</p>
          </div>
        </div>}
    </div>;
}
// Tab component for main navigation
function MainTab({
  active,
  onClick,
  icon,
  label
}) {
  return <button className={`px-6 py-3 font-medium transition-colors flex items-center ${active ? 'text-white border-b-2 border-white' : 'text-blue-300 hover:text-white'}`} onClick={onClick} data-id="element-8983" data-mp="3ki14p1w4anho">
      <span className="mr-2" data-id="element-8984" data-mp="1q38gvp1h710a">{icon}</span>
      {label}
    </button>;
}